import { Controller } from "@hotwired/stimulus"
import { badgeClasses } from '@/utils/badge_classes'

const STORAGE_KEY = "playbook_expanded_tasks"
const MODE_KEY = "playbook_expand_all_mode"

export default class extends Controller {
  static values = { url: String, expanded: Boolean, accountId: String, taskId: String, page: { type: Number, default: 1 } }
  static targets = ["caret", "toggle"]

  connect() {
    // SortableJS disconnect/reconnects controllers when moving a <tbody>.
    // If detail rows already exist from the drag, just sync state — don't re-fetch.
    if (this.#detailRows().length > 0) {
      this.expandedValue = true
      this.#flipCaret("down", "up")
      return
    }

    // In expand-all mode, the collapse-all controller orchestrates a single bulk fetch.
    if (sessionStorage.getItem(MODE_KEY) === "1") return

    if (this.#isPage1() && this.#storedTaskIds().includes(this.taskIdValue)) {
      this.#expand()
    }
  }

  toggle(event) {
    event.preventDefault()
    if (this.expandedValue) {
      this.collapse()
    } else {
      this.#expand()
    }
  }

  expand() {
    if (this.expandedValue) return Promise.resolve()
    return this.#expand({ collapseSiblings: false })
  }

  collapse() {
    this.#detailRows().forEach((r) => r.remove())
    this.expandedValue = false
    this.#flipCaret("up", "down")
    this.#removeFromStorage()
  }

  applyExpandedHtml(html) {
    if (this.expandedValue) return
    this.element.insertAdjacentHTML("afterend", html)
    this.expandedValue = true
    this.#flipCaret("down", "up")
  }

  // Called by the parent row's frequency-dropdown controller (via outlet)
  // after a successful frequency change, so any expanded sub-rows reflect
  // the new value without a refetch. The server-side propagation
  // (Admin::TeamPlaybook::SettingsController#propagate_frequency_to_group!)
  // mirrors this — a page refresh would land on the same state.
  setRelatedFrequency({ name, color }) {
    this.#frequencyCells().forEach((cell) => {
      cell.innerHTML = this.#frequencyBadgeHTML(name, color)
    })
  }

  // Returns a snapshot for revert on PATCH failure. Caller passes the
  // returned array back to restoreRelatedFrequencies.
  snapshotRelatedFrequencies() {
    return this.#frequencyCells().map((cell) => ({ cell, html: cell.innerHTML }))
  }

  restoreRelatedFrequencies(snapshot) {
    snapshot.forEach(({ cell, html }) => { cell.innerHTML = html })
  }

  #expand({ collapseSiblings = true } = {}) {
    if (collapseSiblings) this.#collapseSiblings()

    return fetch(this.urlValue, {
      headers: { "Accept": "text/html" }
    })
      .then((response) => {
        if (response.redirected) {
          window.location.href = response.url
          return null
        }
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        return response.text()
      })
      .then((html) => {
        if (html === null) return
        this.element.insertAdjacentHTML("afterend", html)
        this.expandedValue = true
        this.#flipCaret("down", "up")
        this.#addToStorage()
      })
      .catch((error) => {
        console.error("[TaskDetails] Failed to load:", error)
      })
  }

  #collapseSiblings() {
    document
      .querySelectorAll(`[data-controller*='team-playbook--task-details']`)
      .forEach((el) => {
        const ctrl = this.application.getControllerForElementAndIdentifier(el, "team-playbook--task-details")
        if (ctrl && ctrl !== this && ctrl.expandedValue) {
          ctrl.collapse()
        }
      })
  }

  #detailRows() {
    const tbody = this.element.parentNode
    if (!tbody) return []
    return tbody.querySelectorAll(`tr.task-detail-row[data-parent-task-id="${this.taskIdValue}"]`)
  }

  #frequencyCells() {
    return Array.from(this.#detailRows())
      .map((row) => row.querySelector("[data-team-playbook-task-details-frequency-cell]"))
      .filter(Boolean)
  }

  #frequencyBadgeHTML(name, color) {
    if (!name) return ""
    const safeColor = color || ""
    return `<span class="${badgeClasses({ variant: null })}" style="background-color: ${safeColor}">${name}</span>`
  }

  #flipCaret(from, to) {
    if (!this.hasCaretTarget) return
    this.caretTarget.classList.replace(`fa-chevron-${from}`, `fa-chevron-${to}`)
  }

  #isPage1() {
    return this.pageValue === 1
  }

  #storedTaskIds() {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "[]")
    } catch {
      return []
    }
  }

  #addToStorage() {
    const ids = this.#storedTaskIds().filter((id) => id !== this.taskIdValue)
    ids.push(this.taskIdValue)
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ids)) } catch { /* noop */ }
  }

  #removeFromStorage() {
    const ids = this.#storedTaskIds().filter((id) => id !== this.taskIdValue)
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ids)) } catch { /* noop */ }
  }
}
